import { GetString } from 'fluent-react';
import React from 'react';
import styled from 'styled-components';
import { normalFontWeight } from '../../cssVariables';
import {
  Location,
  NewProductsComparisonCountries,
  NewProductsCountry,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { formatTradeValue } from '../../numberFormatters';
import Loading from '../../sharedComponents/GraphLoading';
import { getCountryName } from '../../Utils';
import { smallMediaQuery } from '../Grid';
import { primaryColor2 } from '../Utils';

const borderColor = '#999';
const highlightBgColor = '#ffedcb';

const Title = styled.h3`
  color: ${primaryColor2};
  font-weight: ${normalFontWeight};
  text-align: center;
  margin-bottom: 0;

  @media ${smallMediaQuery} {
    font-size: 0.9rem;
  }
`;

const TableContainer = styled.div`
  display: grid;
  grid-template-rows: 4rem auto auto auto auto;
  grid-template-columns: auto auto auto auto;
`;

const LoadingContainer = styled.div`
  grid-row: 2;
  grid-column: span 4;
  height: 7rem;
  position: relative;

  @media ${smallMediaQuery} {
    height: 5rem;
  }
`;

const TableCell = styled.div`
  display: flex;
  border-bottom: 1px solid ${borderColor};
  padding: 6px 6px 6px 6px;
  align-items: flex-end;
  font-size: 0.8rem;

  @media ${smallMediaQuery} {
    font-size: 0.7rem;
  }
`;

const TableBodyCell = styled(TableCell)`
  align-items: center;
`;

const TableBodyDataCell = styled(TableBodyCell)`
  justify-content: center;
  border-right: 1px solid ${borderColor};
`;

const TableBodyDataEndCell = styled(TableCell)`
  justify-content: center;
`;

const CountryCell = styled(TableCell)`
  border-right: 1px solid ${borderColor};
`;

const SelectedCountryCell = styled(CountryCell)`
  color: orange;
  background-image: repeating-linear-gradient(135deg, white, white 5px, ${highlightBgColor} 0, ${highlightBgColor} 6px);
`;

const LabelCell = styled(TableCell)`
  text-transform: uppercase;
  font-size: 0.6rem;
  position: relative;

  @media ${smallMediaQuery} {
    font-size: 0.5rem;
  }
`;

const RotatedLabel = styled.div`
  white-space: nowrap;
  transform: rotate(-45deg);
  position: absolute;
  top: 1.3rem;

  @media ${smallMediaQuery} {
    left: -9px;
  }
`;

const query = gql`
  query NewProductsSideBar($id: ID!, $year: Int!, $quantity: Int!) {
    location(id: $id) {
      id
      shortName
      nameAbbr
      thePrefix
    }
    newProductsCountry(location: $id, year: $year) {
      newProductExportValue
      newProductExportValuePerCapita
      newProductCount
    }
    newProductsComparisonCountries(location: $id, year: $year, quantity: $quantity) {
      newProductExportValue
      newProductExportValuePerCapita
      newProductCount
      location {
        id
        shortName
        nameAbbr
        thePrefix
      }
    }
  }
`;

interface SuccessResponse {
  location: null | {
    id: Location['id'],
    shortName: Location['shortName'],
    nameAbbr: Location['nameAbbr'],
    thePrefix: Location['thePrefix'],
  };
  newProductsCountry: null | {
    newProductExportValue: NewProductsCountry['newProductExportValue'],
    newProductExportValuePerCapita: NewProductsCountry['newProductExportValuePerCapita'],
    newProductCount: NewProductsCountry['newProductCount'],
  };
  newProductsComparisonCountries: null | Array<{
    newProductExportValue: NewProductsComparisonCountries['newProductExportValue'],
    newProductExportValuePerCapita: NewProductsComparisonCountries['newProductExportValuePerCapita'],
    newProductCount: NewProductsComparisonCountries['newProductCount'],
    location: null | {
      id: Location['id'],
      shortName: Location['shortName'],
      nameAbbr: Location['nameAbbr'],
      thePrefix: Location['thePrefix'],
    },
  }>;
}

interface Variables {
  id: string;
  year: number;
  quantity: number;
}

interface Props {
  countryId: string;
  year: number;
  callBackYear: number;
  getFluentString: GetString;
}

const CountryProductComparisonChart = (props: Props) => {
  const {
    countryId, year, callBackYear, getFluentString,
  } = props;

  const variables: Variables = {
    id: countryId,
    year,
    quantity: 3,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = (
      <LoadingContainer>
        <Loading />
      </LoadingContainer>
    );
  } else if (error !== undefined) {
    console.error(error);
    out = null;
  } else if (data !== undefined) {
    const {location, newProductsCountry, newProductsComparisonCountries} = data;
    if (location === null || newProductsCountry === null) {
      out = null;
    } else {
      const {
        newProductExportValue,
        newProductExportValuePerCapita,
        newProductCount,
      } = newProductsCountry;
      const {nameInChart: countryShortName} = getCountryName({
        shortName: location.shortName, thePrefix: Boolean(location.thePrefix), nameAbbr: location.nameAbbr,
      });
      interface CountryInTable {
        id: string;
        name: string;
        numNewProducts: number;
        value: number | null;
        formattedValue: string;
        valuePerCapita: number | null;
        formattedValuePerCapita: string;
        isPrimaryCountry: boolean;
      }
      let comparisonLocationsOutput: Array<React.ReactElement<any>> | null;
      if (newProductsComparisonCountries === null) {
        comparisonLocationsOutput = null;
      } else {
        const formatMonetaryValue = (value: null | number) => (value === null) ? '$0' : formatTradeValue(value);
        const otherCountries = newProductsComparisonCountries.map(elem => {
          const {nameInChart: shortName} = getCountryName({
            shortName: elem.location ? elem.location.shortName : '',
            thePrefix: elem.location ? Boolean(elem.location.thePrefix) : false,
            nameAbbr: elem.location ? elem.location.nameAbbr : '',
          });
          const output: CountryInTable = {
            id: elem.location ? elem.location.id : '',
            name: shortName,
            numNewProducts: elem.newProductCount ? elem.newProductCount : 0,
            value: elem.newProductExportValue,
            formattedValue: formatMonetaryValue(elem.newProductExportValue),
            valuePerCapita: elem.newProductExportValuePerCapita,
            formattedValuePerCapita: formatMonetaryValue(elem.newProductExportValuePerCapita),
            isPrimaryCountry: false,
          };
          return output;
        });
        const thisCountry: CountryInTable = {
          id: location.id,
          name: countryShortName,
          numNewProducts: newProductCount ? newProductCount : 0,
          value: newProductExportValue,
          formattedValue: formatMonetaryValue(newProductExportValue),
          valuePerCapita: newProductExportValuePerCapita,
          formattedValuePerCapita: formatMonetaryValue(newProductExportValuePerCapita),
          isPrimaryCountry: true,
        };
        const countriesInTable = [...otherCountries, thisCountry].sort((a, b) => {
          // Sort by number of new products (descending order) and then by
          // export value of new products (descending order):
          const aNumNewProducts = a.numNewProducts;
          const bNumNewProducts = b.numNewProducts;
          if (aNumNewProducts > bNumNewProducts) {
            return -1;
          } else if (aNumNewProducts < bNumNewProducts) {
            return 1;
          } else {
            const aPerCapitaValue = a.valuePerCapita;
            const bPerCapitaValue = b.valuePerCapita;
            if (aPerCapitaValue === null || bPerCapitaValue === null) {
              return 0;
            } else if (aPerCapitaValue > bPerCapitaValue) {
              return -1;
            } else if (aPerCapitaValue < bPerCapitaValue) {
              return 1;
            } else {
              return 0;
            }
          }
        });
        comparisonLocationsOutput = countriesInTable.map(item => {
          const {id, name, numNewProducts, formattedValue, formattedValuePerCapita, isPrimaryCountry} = item;
          const NameCell = isPrimaryCountry ? SelectedCountryCell : CountryCell;
          return (
            <React.Fragment key={id}>
              <NameCell>{name}</NameCell>
              <TableBodyDataCell>{numNewProducts}</TableBodyDataCell>
              <TableBodyDataCell>{formattedValuePerCapita}</TableBodyDataCell>
              <TableBodyDataEndCell>{formattedValue}</TableBodyDataEndCell>
            </React.Fragment>
          );
        });
      }
      out = (
        <>
          {comparisonLocationsOutput}
        </>
      );
    }
  } else {
    out = null;
  }

  return (
    <>
      <Title>
        {getFluentString('new-products-comparison-table-label-title', {
          'callback-year': `${year - callBackYear}`,
          'year': `${year}`,
        })}
      </Title>
      <TableContainer>

        <LabelCell>
          {getFluentString('new-products-comparison-table-label-country')}
        </LabelCell>
        <LabelCell>
          <RotatedLabel
            dangerouslySetInnerHTML={{
              __html: (getFluentString('new-products-comparison-table-label-new-products')),
            }}
          />
        </LabelCell>
        <LabelCell>
          <RotatedLabel
            dangerouslySetInnerHTML={{
              __html: (getFluentString('new-products-comparison-table-label-usd-per-capita')),
            }}
          />
        </LabelCell>
        <LabelCell>
          <RotatedLabel
            dangerouslySetInnerHTML={{
              __html: (getFluentString('new-products-comparison-table-label-usd-billions')),
            }}
          />
        </LabelCell>
        {out}
      </TableContainer>
    </>
  );
};

export default CountryProductComparisonChart;
